import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Nav, Tab } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { store } from 'app/store';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { useAdmin } from 'selectors/adminSelectors';
import { useAppSettings } from 'selectors/appSettingsSelectors';
import JsonViewer from './JsonViewer';

import { IS_TEST } from 'helpers/consts';
import { useUser } from 'selectors/userSelectors';
import './DebugPanel.scss';

export function showDebugPreview(element: HTMLElement) {
  const panel = document.getElementById('debug_preview_panel');
  if (!panel) return;
  const clone = element.cloneNode(true) as HTMLElement;
  panel.appendChild(clone);
}

// Debug info context
type DebugInfoContextType = [
  Record<string, any>,
  (obj: Record<string, any>) => void,
];
export const DebugInfoContext = React.createContext<
  DebugInfoContextType | undefined
>(undefined);

export function DebugInfoProvider({ children }: { children: React.ReactNode }) {
  const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});
  const updateDebugInfo = useCallback((obj: Record<string, any>) => {
    setDebugInfo((prev) => ({ ...prev, ...obj }));
  }, []);
  return (
    <DebugInfoContext.Provider value={[debugInfo, updateDebugInfo]}>
      {children}
    </DebugInfoContext.Provider>
  );
}

export function useDebugInfo(): DebugInfoContextType {
  const ctx = useContext(DebugInfoContext);
  if (!ctx)
    throw new Error('useDebugInfo must be used within a DebugInfoProvider');
  return ctx;
}

export default function DebugPanel() {
  const { userIsAdmin } = useUser();
  const { debugPanel } = useAdmin();
  const { settings } = useAppSettings();
  const [open, setOpen] = useState<boolean>(false);
  const ctx = useContext(DebugInfoContext);
  const [debugInfo, setDebugInfo] = ctx ? ctx : [{}, () => {}];
  const [activeTab, setActiveTab] = useState<string>('redux');
  const [showFullState, setShowFullState] = useState<boolean>(false);
  const [reduxState, setReduxState] = useState<any>(store.getState());

  function updateState() {
    const fullState = store.getState();
    if (showFullState) {
      setReduxState(fullState);
    } else {
      setReduxState({
        auth: fullState.auth,
        site: fullState.site,
      });
    }
  }

  // Subscribe to store updates when the redux tab is active
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    if (open && activeTab === 'redux') {
      updateState();
      unsubscribe = store.subscribe(updateState);
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [open, activeTab, showFullState]);

  function handleOpen() {
    setOpen(!open);
  }
  function handleBinInfo() {
    const panel = document.getElementById('debug_info_panel');
    if (panel) panel.replaceChildren();
  }
  function handleBinPreview() {
    const panel = document.getElementById('debug_preview_panel');
    if (panel) panel.replaceChildren();
  }
  function handleRefreshRedux() {
    updateState();
  }

  function toggleFullState() {
    setShowFullState(!showFullState);
    handleRefreshRedux();
  }

  return (
    (IS_TEST || userIsAdmin) &&
    debugPanel && (
      <div
        id="debug_container"
        className={classNames('vertical_scrollable', {
          debug_panel_open: open,
        })}
      >
        <Icon
          symbol={open ? IconSymbol.right : IconSymbol.left}
          onClick={handleOpen}
          className="debug_panel_open_icon"
          size={24}
        />
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'redux')}
        >
          <Nav variant="tabs" className="debug_panel_tabs">
            <Nav.Item>
              <Nav.Link eventKey="redux">
                <FormattedMessage id="ADMIN.DEBUG_PANEL.REDUX_STATE" />
              </Nav.Link>
              {/* Refresh button no longer needed, since we have live updates */}
              <Icon
                symbol={IconSymbol.reload}
                onClick={handleRefreshRedux}
                className="debug_panel_icon"
                inline
                size={12}
                hintProps={{ hint: { id: 'ADMIN.DEBUG_PANEL.RELOAD' } }}
              />
              <Icon
                symbol={showFullState ? IconSymbol.minus : IconSymbol.plus}
                onClick={toggleFullState}
                className="debug_panel_icon"
                inline
                size={12}
                hintProps={{
                  hint: { id: 'ADMIN.DEBUG_PANEL.TOGGLE_FULL_STATE' },
                }}
              />
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="settings">
                <FormattedMessage id="ADMIN.DEBUG_PANEL.SETTINGS" />
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="props">
                <FormattedMessage id="ADMIN.DEBUG_PANEL.PROPS" />
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="debug">
                <FormattedMessage id="ADMIN.DEBUG_PANEL.DEBUG" />
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="dragPreview">
                <FormattedMessage id="ADMIN.DEBUG_PANEL.DRAG_PREVIEW" />
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content className="debug_panel_content">
            <Tab.Pane eventKey="redux">
              <JsonViewer data={reduxState} />
            </Tab.Pane>
            <Tab.Pane eventKey="settings">
              <JsonViewer data={settings} />
            </Tab.Pane>
            <Tab.Pane eventKey="props">
              <JsonViewer data={{}} />
            </Tab.Pane>
            <Tab.Pane eventKey="debug">
              <Icon
                symbol={IconSymbol.bin}
                onClick={() => setDebugInfo({})}
                className="debug_panel_icon"
                size={24}
              />
              <div id="debug_info_panel">
                {Object.entries(debugInfo).map(([key, value]) => (
                  <div key={key} style={{ marginBottom: 8 }}>
                    <strong>{key}:</strong>{' '}
                    {typeof value === 'object' ? (
                      <JsonViewer data={value} name={key} />
                    ) : (
                      String(value)
                    )}
                  </div>
                ))}
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey="dragPreview">
              <Icon
                symbol={IconSymbol.bin}
                onClick={handleBinPreview}
                className="debug_panel_icon"
                size={24}
              />
              <div id="debug_preview_panel" />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    )
  );
}
